import { useState, useMemo, useEffect } from "react";
import { Field, Formik, useFormik } from "formik";
import { useSelector } from "react-redux";
import { useLocation, useParams, useOutletContext } from "react-router-dom";
import DiscountInput from "./DiscountInput";
import PhoneInput from "react-phone-input-2";
import * as yup from "yup";
import { toast } from "react-toastify";
import Select from "react-select";

import OrderTableItem from "./OrderTableItem";
import { CountryList } from "./CountryList";
import { discountValue } from "./DiscountValue";
import { parseCurrency } from "../../utils/currency";
import { makeOrder } from "../../utils/ApiCalls";
import { dispatch } from "../../store";
import {
  EmptyImage,
  getThemeColor,
  parseCountries,
  parseDeliveryOptions,
} from "../../utils/constants";

const formSchema = yup.object().shape({
  firstName: yup.string().required("First Name is a required field"),
  lastName: yup.string().required("Last Name is a required field"),
  country: yup.string().required("Country is a required field"),
  postalCode: yup.string().required("Postal Code is a required field"),
  address1: yup.string().required("Address is a required field"),
  town: yup.string().required("Town is a required field"),
  state: yup.string().required("State is a required field"),
  email: yup.string().required("Email is a required field"),
  phoneNumber: yup
    .string()
    .matches(/^\d+$/, "Number is not valid")
    .min(7, "Not a valid phone number")
    .required("Phone Number is a required field"),
});

export default function Checkout() {
  const { state } = useLocation();
  const { deliverySetup, storeID, currency, discounts, theme } =
    useOutletContext();
  const [discount, setDiscount] = useState({});
  const { slug } = useParams();

  const initialValues = {
    firstName: "",
    lastName: "",
    country: "Nigeria",
    postalCode: "",
    address1: "",
    address2: "",
    delivery: "",
    orderNote: "",
    state: "",
    email: "",
    phoneNumber: "",
  };

  const {
    values,
    setFieldValue,
    handleSubmit,
    isValid,
    isValidating,
    touched,
    setErrors,
    submitCount,
    errors,
  } = useFormik({
    validationSchema: formSchema,
    initialValues: initialValues,
    validateOnChange: false,
    validateOnBlur: true,
    validateOnMount: false,
    onSubmit: async () => await handleOrder(),
  });
  const [deliveryData, setDeliveryData] = useState({
    deliveryFee: "",
    deliveryFeePerKG: 0,
    deliveryID: deliverySetup[0]?.deliverySetupId || 0,
    deliveryLocation: deliverySetup[0]?.deliveryLocation || "",
  });

  const productArray = useMemo(() => Object.values(state), []);
  const countryListOptions = useMemo(() => parseCountries(CountryList), []);
  const deliverySetupOptions = useMemo(
    () => parseDeliveryOptions(deliverySetup),
    []
  );

  const handleChange = (key, value) => {
    setFieldValue(key, value);
    if (errors[key]) setErrors({ ...errors, [key]: "" });
  };

  const orderData = {
    order: {
      orderRef: "default",
      discountCode: discount?.discountCode,
      recipientName: values.firstName + " " + values.lastName,
      phoneNumber: values.phoneNumber,
      recipientEmail: values.email,
      addressLine1: values.address1,
      addressLine2: values.address2,
      postalCode: values.postalCode,
      state: values.state,
      town: values.town,
      country: values.country,
      deliveryNote: values.orderNote,
      deliverySetupId: deliveryData.deliveryID,
      storeSlug: slug,
    },
    orderItems: (() => {
      let orderItems = [];
      productArray.forEach((item) => {
        let orderItem = {};
        orderItem.quantity = item.unit;
        orderItem.orderRef = "x";
        orderItem.productId = item.productId;
        orderItem.variations = item.selectedVariation;
        orderItem.imageUrl = item.productImageUrl || EmptyImage;
        orderItems.push(orderItem);
      });

      return orderItems;
    })(),
    storeSlug: slug,
    storeID: storeID,
  };

  const totalProductsData = useMemo(() => {
    let totalPrice = 0;
    let totalWeight = 0;
    let totalUnit = 0;

    productArray.forEach((data) => {
      totalPrice += data?.productPrice * data?.unit;
      // totalWeight += +data?.productWeight * data?.unit;
    });

    return { totalPrice, totalWeight, totalUnit };
  }, [productArray]);

  const { totalPrice, totalWeight } = totalProductsData;

  const discountAmount = discountValue(
    totalPrice,
    discount?.discountAmount,
    discount?.isPercentage
  );

  const finalPrice =
    totalPrice +
    // + deliveryPrice
    discountAmount +
    deliveryData.deliveryFee;

  async function handleOrder() {
    try {
      if (deliverySetup.length > 0 && !values.delivery) {
        toast.error("Please select a delivery location");
        return;
      }

      dispatch.loading.show();
      const response = await makeOrder(orderData);
      dispatch.loading.hide();
      if (response.paymentUrl) {
        dispatch.cart.clear();
        window.location.assign(response.paymentUrl);
      }
    } catch (error) {
      dispatch.loading.hide();
      toast(error);
    }
  }

  useEffect(() => {
    dispatch.loading.hide();
  }, []);

  useEffect(() => {
    if (!isValid) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isValid]);

  return (
    <>
      <div className="checkout-page pt-4">
        <div className="page-wrapper">
          <div id="page-content">
            <div className="container">
              <form onSubmit={handleSubmit} className="checkout-form">
                <div className="row">
                  <div className="col-md-6 col-lg-6">
                    <div className="card card--grey">
                      <div className="card-body">
                        <h2 className="fs-6">SHIPPING ADDRESS</h2>

                        <div className="row mt-2">
                          <div className="col-sm-6">
                            <label className="text-uppercase d-none">
                              First Name:
                            </label>
                            <div className="form-group">
                              <input
                                name="firstName"
                                type="text"
                                onChange={(e) =>
                                  handleChange("firstName", e.target.value)
                                }
                                placeholder="First Name"
                                className="form-control"
                              />
                              {errors.firstName && (
                                <label className="text-danger mt-2">
                                  {errors.firstName}
                                </label>
                              )}
                            </div>
                          </div>
                          <div className="col-sm-6">
                            <label className="text-uppercase d-none">
                              Last Name:
                            </label>
                            <div className="form-group">
                              <input
                                name="lastName"
                                type="text"
                                onChange={(e) =>
                                  handleChange("lastName", e.target.value)
                                }
                                placeholder="Last Name"
                                className="form-control"
                              />
                              {errors.lastName && (
                                <label className="text-danger mt-2">
                                  {errors.lastName}
                                </label>
                              )}
                            </div>
                          </div>
                        </div>
                        <label className="text-uppercase d-none">
                          Phone Number:
                        </label>

                        <div className="form-group">
                          <div className="col-sm-12">
                            <PhoneInput
                              country={"ng"}
                              value={values.phoneNumber}
                              onChange={(e) => handleChange("phoneNumber", e)}
                            />
                            {errors.phoneNumber && (
                              <label className="text-danger mt-2">
                                {errors.phoneNumber}
                              </label>
                            )}
                          </div>
                        </div>
                        <label className="text-uppercase d-none">
                          Country:
                        </label>
                        <div className="form-group select-wrapper">
                          <Select
                            options={countryListOptions}
                            defaultValue="Nigeria"
                            placeholder="Select a Country"
                            name="country"
                            onChange={(e) => handleChange("country", e.value)}
                            value={{
                              label: values.country,
                              value: values.country,
                            }}
                          />

                          {errors.country && (
                            <label className="text-danger mt-2">
                              {errors.country}
                            </label>
                          )}
                        </div>
                        <div className="row">
                          <div className="col-sm-6">
                            <label className="text-uppercase d-none">
                              State:
                            </label>
                            <div className="form-group select-wrapper">
                              <input
                                type="text"
                                name="state"
                                onChange={(e) =>
                                  handleChange("state", e.target.value)
                                }
                                placeholder="State"
                                className="form-control"
                              />
                              {errors.state && (
                                <label className="text-danger mt-2">
                                  {errors.state}
                                </label>
                              )}
                            </div>
                          </div>
                          <div className="col-sm-6">
                            <label className="text-uppercase d-none">
                              Email:
                            </label>
                            <div className="form-group select-wrapper">
                              <input
                                type="email"
                                onChange={(e) =>
                                  handleChange("email", e.target.value)
                                }
                                name="email"
                                placeholder="Email"
                                className="form-control"
                              />
                              {errors.email && (
                                <label className="text-danger mt-2">
                                  {errors.email}
                                </label>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-sm-6">
                            <label className="text-uppercase d-none">
                              Town:
                            </label>
                            <div className="form-group">
                              <input
                                type="text"
                                onChange={(e) =>
                                  handleChange("town", e.target.value)
                                }
                                name="town"
                                placeholder="Town"
                                className="form-control"
                              />
                              {errors.town && (
                                <label className="text-danger mt-2">
                                  {errors.town}
                                </label>
                              )}
                            </div>
                          </div>
                          <div className="col-sm-6">
                            <label className="text-uppercase d-none">
                              Zip/postal code:
                            </label>
                            <div className="form-group">
                              <input
                                type="text"
                                name="postalCode"
                                onChange={(e) =>
                                  handleChange("postalCode", e.target.value)
                                }
                                placeholder="Zip/postal code"
                                className="form-control"
                              />
                              {errors.postalCode && (
                                <label className="text-danger mt-2">
                                  {errors.postalCode}
                                </label>
                              )}
                            </div>
                          </div>
                        </div>
                        <label className="text-uppercase d-none">
                          Address 1:
                        </label>

                        <div className="form-group">
                          <input
                            type="text"
                            name="address1"
                            onChange={(e) =>
                              handleChange("address1", e.target.value)
                            }
                            placeholder="Address Line 1"
                            className="form-control"
                          />
                          {errors.address1 && (
                            <label className="text-danger mt-2">
                              {errors.address1}
                            </label>
                          )}
                        </div>
                        <label className="text-uppercase d-none">
                          Address 2:
                        </label>
                        <div className="form-group">
                          <input
                            type="text"
                            onChange={(e) =>
                              handleChange("address2", e.target.value)
                            }
                            name="Address2"
                            placeholder="Address Line 2"
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 col-lg-6 mt-2 mt-md-0">
                    {deliverySetup.length > 0 && (
                      <div className="card card--grey">
                        <div className="card-body">
                          <h2 className="pb-1 fs-6">DELIVERY REGION</h2>
                          <Select
                            options={deliverySetupOptions}
                            name="delivery"
                            placeholder="Select a Delivery Option"
                            className="custom-form-select"
                            isSearchable
                            onChange={(e) => {
                              handleChange(
                                "delivery",
                                `${e.deliveryLocation} ${e.deliveryDuration}`
                              );
                              setDeliveryData({
                                deliveryFee: e.deliveryFee,
                                deliveryFeePerKG: e.deliveryFeePerKg,
                                deliveryID: e.deliverySetupId,
                              });
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="card card--grey mt-2">
                      <div className="card-body">
                        <h2 className="fs-6">Order Comment</h2>
                        <label className="text-uppercase d-none">
                          Write a comment here:
                        </label>

                        <textarea
                          onChange={(e) =>
                            handleChange("orderNote", e.target.value)
                          }
                          name="orderNote"
                          rows="4"
                          placeholder="Order Note"
                          className="form-control textarea--height-200"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12 col-lg-12 mt-2 mt-lg-0">
                    <h2 className="title fs-6">ORDER SUMMARY</h2>
                    <div className="table-responsive order-table style1">
                      <table className="table table-bordered align-middle table-hover text-center mb-1">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th className="text-start">Name</th>
                            <th>Price</th>
                            <th>Qty</th>
                            <th>Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productArray.map((data) => (
                            <OrderTableItem data={data} />
                          ))}
                        </tbody>
                        <tfoot className="font-weight-600">
                          <tr>
                            <td colspan="4" className="text-end fw-bolder">
                              Delivery Fee{" "}
                            </td>
                            <td className="fw-bolder">
                              {parseCurrency(
                                deliveryData.deliveryFee,
                                currency
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td colspan="4" className="text-end fw-bolder">
                              Discount{" "}
                            </td>
                            <td className="fw-bolder">
                              {parseCurrency(discountAmount, currency)}
                            </td>
                          </tr>

                          <tr>
                            <td colspan="4" className="text-end fw-bolder">
                              Total
                            </td>
                            <td className="fw-bolder">
                              {parseCurrency(finalPrice, currency)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    <DiscountInput
                      discountList={discounts}
                      discount={discount}
                      storeID={storeID}
                      theme={theme}
                      setDiscount={setDiscount}
                    />

                    <br />
                    <div>
                      <div style={{ width: "100%", margin: "auto" }}>
                        <label
                          class="form-check-label position-relative"
                          style={{ textAlign: "center" }}>
                          By clicking the pay button below, you have read and
                          agreed to our{" "}
                          <a
                            href="https://billsclan.com/terms"
                            rel="noopener noreferrer"
                            target="_blank"
                            style={{
                              color: getThemeColor(theme).backgroundColor,
                            }}>
                            terms
                          </a>{" "}
                          and{" "}
                          <a
                            href="https://billsclan.com/privacy"
                            rel="noopener noreferrer"
                            target="_blank"
                            style={{
                              color: getThemeColor(theme).backgroundColor,
                            }}>
                            privacy
                          </a>{" "}
                          policy
                        </label>
                      </div>
                    </div>
                    <br />
                    <div className="order-button-payment mt-3 clearfix">
                      <button
                        disabled={finalPrice < 0}
                        type="submit"
                        className={`cartCheckout fs-6 btn btn-lg rounded w-100 fw-600 text-white ${theme}`}>
                        Proceed to Pay
                      </button>
                    </div>
                    <br />
                  </div>
                </div>
              </form>
            </div>
          </div>

          <span id="site-scroll">
            <i className="icon an an-chevron-up"></i>
          </span>

          <div className="modalOverly"></div>
        </div>
      </div>
    </>
  );
}
